import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { addAnswer, clearAnsweres } from "../../../../redux/survey/actions";
import GridSpotImage from "../../../common/components/GridSpotImage";
import { waitForFileProcessing } from "../../../helpers/fileProcessing";
import {
  isGridspotMultiSelect,
  readGridspotSelectedOptionIds,
  toGridspotAnswerValue,
  toggleGridspotAnswerValue,
} from "../../../helpers/gridspot";
import { filterVisibleSurveyQuestions } from "../../../helpers/surveyHelper";

const GridSpot = (props) => {
  const {
    headTonext,
    question,
    answeres,
    addAnswer_,
    pageNo,
    surveyType,
    allQuestionsOriginal,
    setAllQuestions,
    setTotalPage,
    isGreyOut,
    clearAnsweres_,
    survey_theme,
  } = props;

  const selectingRef = useRef(false);
  const isMultiSelect = isGridspotMultiSelect(question);

  useEffect(() => {
    selectingRef.current = false;
  }, [question?._id]);

  const answer = () => {
    if (!(question._id in answeres)) {
      return { value: isMultiSelect ? [] : "" };
    }
    // Do not treat [] as missing — empty multi-select is a valid cleared answer.
    const stored = answeres[question._id];
    if (stored?.value === undefined || stored?.value === null) {
      return { value: isMultiSelect ? [] : "" };
    }
    return stored;
  };

  const applyTabletJourneyFilter = (value) => {
    if (surveyType !== "tablet" || pageNo !== 1) return;
    clearAnsweres_();
    const percentage = (Number(value) / Number(question?.maxScore)) * 100;
    const filteredQuestion = filterVisibleSurveyQuestions(
      percentage >= 70
        ? allQuestionsOriginal
            .filter((item) => item._id !== question._id)
            .filter(
              (item) =>
                item.journey_type === "neutral" ||
                item.journey_type === "positive",
            )
        : allQuestionsOriginal
            .filter((item) => item._id !== question._id)
            .filter(
              (item) =>
                item.journey_type === "neutral" ||
                item.journey_type === "negative",
            ),
    );
    setTotalPage(filteredQuestion.length + 1);
    setAllQuestions([question, ...filteredQuestion]);
  };

  /** Sum of selected option scores — same journey input as checkbox multiselect. */
  const selectedOptionsScore = (ids) =>
    (question.options || []).reduce((sum, option) => {
      if (!ids.some((id) => String(id) === String(option._id))) return sum;
      return sum + Number(option.value || 0);
    }, 0);

  const selectOption = async (option, point) => {
    if (isGreyOut || selectingRef.current) return;
    selectingRef.current = true;
    try {
      await waitForFileProcessing();

      if (isMultiSelect) {
        const nextValue = toggleGridspotAnswerValue(answer().value, option._id);
        // Filter journey from the full selection (like checkbox), then re-save
        // the answer because applyTabletJourneyFilter clears Redux answers.
        applyTabletJourneyFilter(selectedOptionsScore(nextValue));
        addAnswer_({
          id: question._id,
          value: nextValue,
          type: "id",
          gridspot_point: point,
        });
        // Same as MultipleSelect: apply hide_questions on every toggle so Next
        // advances over an already-filtered question list.
        const hideIndex = (question.options || [])
          .filter((opt) =>
            nextValue.some((id) => String(id) === String(opt._id)),
          )
          .flatMap((opt) => opt?.hide_questions || []);
        await headTonext(undefined, hideIndex, nextValue);
        return;
      }

      applyTabletJourneyFilter(option.value);
      const selectedValue = toGridspotAnswerValue(option._id);
      addAnswer_({
        id: question._id,
        value: selectedValue,
        type: "id",
        // Where the respondent actually tapped. A gridspot can cover many cells,
        // so the option id alone does not record the position.
        gridspot_point: point,
      });
      // Same signature as Radio: route, hide_questions, selected option id(s).
      await headTonext(undefined, option?.hide_questions, selectedValue);
    } catch {
      // waitForFileProcessing or navigation errors — allow retry.
    } finally {
      selectingRef.current = false;
    }
  };

  const selectedIds = readGridspotSelectedOptionIds(answer().value);

  return (
    <Box
      style={{ cursor: isGreyOut ? "not-allowed" : "default", width: "100%" }}
    >
      <GridSpotImage
        question={question}
        selectedOptionIds={selectedIds}
        disabled={Boolean(isGreyOut)}
        onSelect={selectOption}
        accentColor={survey_theme?.primaryColor}
        alt={
          question?.text
            ? String(question.text).replace(/<[^>]+>/g, "")
            : "Survey image"
        }
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  answeres: state.survey.answeres,
  survey_theme: state.survey.survey_theme,
});

const mapDispatchToProps = (dispatch) => ({
  addAnswer_: (data) => dispatch(addAnswer(data)),
  clearAnsweres_: () => dispatch(clearAnsweres()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GridSpot);
